import { Box, Stack } from '@mui/material';
import { FC, useState, useCallback, useMemo } from 'react';
import { translateReference } from '../../../../core/legacy/utils/translations';
import { gradedRubricsForFilterPanelQuery } from '../../../../core/catalogs/queries';
import { selectedGradeReferenceQuery } from '../../../../core/networkMap/queries';
import referenceResolver from '../../../../core/legacy/utils/referenceResolver';
import { useAppSelector } from '../../../../types';
import { theme } from '../../../legacy/style/theme';

type Props = {
    onSelect: (key: string) => void;
};

const GradeFiltersPanel: FC<Props> = ({ onSelect }) => {
    const rubricItems = useAppSelector(gradedRubricsForFilterPanelQuery);
    const selectedGradeReference = useAppSelector(selectedGradeReferenceQuery);
    const selectedKey = selectedGradeReference ? referenceResolver(selectedGradeReference) : 'AGGREGATE';

    const [isOpen, setIsOpen] = useState(false);
    const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());

    const toggleParent = useCallback((parentId: string) => {
        setExpandedParents((prev) => {
            const next = new Set(prev);
            if (next.has(parentId)) next.delete(parentId);
            else next.add(parentId);
            return next;
        });
    }, []);

    const handleSelect = useCallback(
        (key: string) => {
            onSelect(key);
            setIsOpen(false);
        },
        [onSelect],
    );

    const selectedLabel = useMemo(() => {
        if (selectedKey === 'AGGREGATE') return translateReference('AGGREGATE');
        for (const { groups, flatRefs } of rubricItems) {
            for (const { children } of groups) {
                const child = children.find((c) => c.key === selectedKey);
                if (child) return child.name;
            }
            const flat = flatRefs.find((r) => r.key === selectedKey);
            if (flat) return translateReference(flat);
        }
        return selectedKey;
    }, [selectedKey, rubricItems]);

    const itemSx = (selected: boolean, extraPl?: number) => ({
        cursor: 'pointer',
        padding: '4px 8px',
        paddingLeft: extraPl ? `${extraPl * 8}px` : '8px',
        borderRadius: '4px',
        fontSize: '14px',
        backgroundColor: selected ? theme.color.blue.medium : 'transparent',
        color: selected ? theme.color.white.main : theme.color.black.dark,
        '&:hover': {
            backgroundColor: selected ? theme.color.blue.medium : theme.color.blue.light,
        },
    });

    return (
        <Box sx={{ position: 'relative', width: '100%' }}>
            {/* Trigger — shows selected label, click to open/close */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => setIsOpen((prev) => !prev)}
                sx={{
                    cursor: 'pointer',
                    padding: '16.5px 14px',
                    border: '1px solid rgba(0,0,0,0.23)',
                    borderRadius: '4px',
                    backgroundColor: theme.color.white.main,
                    fontSize: '14px',
                    color: theme.color.black.dark,
                    userSelect: 'none',
                }}
            >
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedLabel}
                </Box>
                <Box sx={{ fontSize: '10px', ml: 1, color: theme.color.grey.main }}>
                    {isOpen ? '▲' : '▼'}
                </Box>
            </Stack>

            {/* Dropdown list */}
            {isOpen && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        backgroundColor: theme.color.white.main,
                        border: `1px solid ${theme.color.grey.light}`,
                        borderRadius: '4px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        maxHeight: '400px',
                        overflowY: 'auto',
                        mt: '2px',
                    }}
                >
                    {/* AGGREGATE */}
                    <Box
                        sx={itemSx(selectedKey === 'AGGREGATE')}
                        onClick={() => handleSelect('AGGREGATE')}
                    >
                        {translateReference('AGGREGATE')}
                    </Box>

                    {rubricItems.map(({ rubric, groups, flatRefs }) => (
                        <Box key={rubric.id}>
                            {/* Rubric name — non-selectable header */}
                            <Box
                                sx={{
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    color: theme.color.grey.main,
                                    padding: '8px 8px 2px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    userSelect: 'none',
                                }}
                            >
                                {rubric.name}
                            </Box>

                            {/* Hierarchical groups */}
                            {groups.map(({ parent, children }) => (
                                <Box key={parent.id}>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                                        onClick={() => toggleParent(parent.id)}
                                    >
                                        <Box
                                            sx={{
                                                px: '6px',
                                                fontSize: '12px',
                                                color: theme.color.grey.main,
                                                minWidth: '18px',
                                                textAlign: 'center',
                                            }}
                                        >
                                            {expandedParents.has(parent.id) ? '−' : '+'}
                                        </Box>
                                        <Box
                                            sx={{
                                                padding: '4px 8px',
                                                fontSize: '14px',
                                                fontWeight: 'bold',
                                                flex: 1,
                                                color: theme.color.black.dark,
                                            }}
                                        >
                                            {parent.name}
                                        </Box>
                                    </Stack>
                                    {expandedParents.has(parent.id) &&
                                        children.map((child) => (
                                            <Box
                                                key={child.id}
                                                sx={itemSx(selectedKey === child.key, 4)}
                                                onClick={() => handleSelect(child.key)}
                                            >
                                                {child.name}
                                            </Box>
                                        ))}
                                </Box>
                            ))}

                            {/* Flat refs (non-hierarchical rubrics) */}
                            {flatRefs.map((ref) => (
                                <Box
                                    key={ref.id}
                                    sx={itemSx(selectedKey === ref.key)}
                                    onClick={() => handleSelect(ref.key)}
                                >
                                    {translateReference(ref)}
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default GradeFiltersPanel;
